import type { PurchaseOrderStatus } from './purchaseTypes';

export type PurchaseFieldErrors = Record<string, string>;

export type PurchaseFormValidation =
  | { ok: true }
  | { ok: false; fieldErrors: PurchaseFieldErrors; message: string };

export type PurchaseOrderFormInput = {
  supplierId: string | null;
  notes?: string | null;
  expectedDelivery?: string | null;
  items: Array<{
    productId: string | null;
    quantity: number;
    unitCost: number | null;
    unitOfMeasure?: string | null;
    /** #713: variante de destino (null = parent). */
    variantId?: string | null;
  }>;
};

const MAX_QTY = 1_000_000;
const MAX_COST = 99_999_999.99;
const FRACTIONAL_UOM = new Set(['kg', 'g', 'gr', 'l', 'lt', 'ml', 'cc', 'm', 'cm']);

export function canSendPurchaseOrder(status: PurchaseOrderStatus): boolean {
  return status === 'draft';
}

export function canReceivePurchaseOrder(status: PurchaseOrderStatus): boolean {
  return status === 'sent' || status === 'partial';
}

export function canVoidPurchaseOrder(status: PurchaseOrderStatus): boolean {
  return status === 'draft' || status === 'sent';
}

export type PurchaseCloseLine = { quantity: number; receivedQuantity: number };

export function canClosePurchaseOrder(
  status: PurchaseOrderStatus,
  items: PurchaseCloseLine[],
): boolean {
  if (status !== 'partial' && status !== 'received') return false;
  const anyReceived = items.some((item) => item.receivedQuantity > 0);
  if (!anyReceived) return false;
  const hasShortage = items.some((item) => item.receivedQuantity < item.quantity);
  const hasOverage = items.some((item) => item.receivedQuantity > item.quantity);
  if (status === 'partial') return hasShortage;
  return hasShortage || hasOverage;
}

export function validateCloseReason(reason: string | null | undefined): PurchaseFormValidation {
  const trimmed = (reason ?? '').trim();
  if (trimmed.length < 3) {
    return { ok: false, fieldErrors: { closeReason: 'Indicá un motivo (mínimo 3 caracteres).' }, message: 'Indicá un motivo (mínimo 3 caracteres).' };
  }
  if (trimmed.length > 200) {
    return { ok: false, fieldErrors: { closeReason: 'El motivo no puede superar 200 caracteres.' }, message: 'El motivo no puede superar 200 caracteres.' };
  }
  return { ok: true };
}

export function validateVoidReason(reason: string | null | undefined): PurchaseFormValidation {
  const trimmed = (reason ?? '').trim();
  if (trimmed.length < 3) {
    return { ok: false, fieldErrors: { voidReason: 'Indicá un motivo (mínimo 3 caracteres).' }, message: 'Indicá un motivo (mínimo 3 caracteres).' };
  }
  if (trimmed.length > 200) {
    return { ok: false, fieldErrors: { voidReason: 'El motivo no puede superar 200 caracteres.' }, message: 'El motivo no puede superar 200 caracteres.' };
  }
  return { ok: true };
}

export function formatPurchaseOrderNumber(year2: number, seq: number): string {
  return `OC-${String(year2).padStart(2, '0')}-${String(seq).padStart(4, '0')}`;
}

export function validatePurchaseOrderForm(input: PurchaseOrderFormInput): PurchaseFormValidation {
  const fieldErrors: PurchaseFieldErrors = {};

  if (!input.supplierId) {
    fieldErrors.supplierId = 'Elegí un proveedor.';
  }

  const notes = (input.notes ?? '').trim();
  if (notes.length > 500) {
    fieldErrors.notes = 'Las notas no pueden superar 500 caracteres.';
  }

  const expected = (input.expectedDelivery ?? '').trim();
  if (expected && !/^\d{4}-\d{2}-\d{2}$/.test(expected)) {
    fieldErrors.expectedDelivery = 'Fecha inválida.';
  }

  if (!input.items.length) {
    fieldErrors.items = 'Agregá al menos un producto.';
  }

  input.items.forEach((item, index) => {
    if (!item.productId) {
      fieldErrors[`items.${index}.productId`] = 'Elegí un producto con stock.';
    }
    if (!Number.isFinite(item.quantity) || item.quantity <= 0) {
      fieldErrors[`items.${index}.quantity`] = 'Ingresá una cantidad mayor a cero.';
    } else if (item.quantity > MAX_QTY) {
      fieldErrors[`items.${index}.quantity`] = 'Ingresá una cantidad mayor a cero.';
    } else {
      const allowsFraction = item.unitOfMeasure ? FRACTIONAL_UOM.has(item.unitOfMeasure) : true;
      if (!allowsFraction && !Number.isInteger(item.quantity)) {
        fieldErrors[`items.${index}.quantity`] = 'Este producto no admite decimales.';
      }
    }
    if (item.unitCost == null || !Number.isFinite(item.unitCost) || item.unitCost < 0) {
      fieldErrors[`items.${index}.unitCost`] = 'Ingresá un costo válido (ej. 120 o 120,50).';
    } else if (item.unitCost > MAX_COST) {
      fieldErrors[`items.${index}.unitCost`] = 'Ingresá un costo válido (ej. 120 o 120,50).';
    }
  });

  const keys = Object.keys(fieldErrors);
  if (keys.length === 0) return { ok: true };
  return { ok: false, fieldErrors, message: fieldErrors[keys[0]] };
}

export function validateReceptionForm(
  items: Array<{ quantity: number; pending?: number; unitCost?: number | null }>,
): PurchaseFormValidation {
  const withQty = items.filter((item) => Number.isFinite(item.quantity) && item.quantity > 0);
  if (withQty.length === 0) {
    return { ok: false, fieldErrors: { items: 'Ingresá cuánto llegó.' }, message: 'Ingresá cuánto llegó.' };
  }
  // BUG-23: Validar que no se reciba más de lo pendiente
  for (const item of withQty) {
    if (item.pending != null && item.quantity > item.pending) {
      return {
        ok: false,
        fieldErrors: { items: `Recibiste más de lo pendiente (pendiente: ${item.pending}).` },
        message: `Recibiste más de lo pendiente (pendiente: ${item.pending}).`,
      };
    }
  }
  // BUG-26: Validar que todo item con cantidad tenga costo
  for (const item of withQty) {
    if (item.unitCost == null || !Number.isFinite(item.unitCost) || item.unitCost < 0) {
      return {
        ok: false,
        fieldErrors: { items: 'Ingresá el costo unitario de cada item recibido.' },
        message: 'Ingresá el costo unitario de cada item recibido.',
      };
    }
  }
  return { ok: true };
}

// ─── #694: Recepción por variante ───────────────────────────────────────────

/** Porción recibida de un ítem asignada a una variante (o al parent si variantId es null). */
export type ReceptionVariantSlice = {
  variantId: string | null;
  quantity: number;
};

/**
 * Validación de distribución entre variantes (#694):
 * - sin distribución → recepción al parent como hoy (compatibilidad #5);
 * - si se distribuye, la suma debe igualar la cantidad recibida del ítem.
 */
export function validateVariantDistribution(
  params: {
    quantity: number;
    variants: Array<{ id: string }>;
    distribution: ReceptionVariantSlice[];
  },
): PurchaseFormValidation {
  const { quantity, variants, distribution } = params;

  if (variants.length === 0) return { ok: true };

  // Sin distribución (o todo en 0): recepción al parent como hoy (backward-compat).
  if (distribution.length === 0) return { ok: true };

  for (const slice of distribution) {
    if (!Number.isFinite(slice.quantity) || slice.quantity < 0) {
      return {
        ok: false,
        fieldErrors: { items: 'Ingresá cantidades válidas (0 o más) en cada variante.' },
        message: 'Ingresá cantidades válidas (0 o más) en cada variante.',
      };
    }
  }

  const assigned = distribution.reduce((sum, slice) => sum + slice.quantity, 0);
  // EPS 1e-6: cantidades decimales (kg) nunca cierran exacto por redondeo de decimales.
  if (Math.abs(assigned - quantity) > 1e-6) {
    return {
      ok: false,
      fieldErrors: { items: `Lo distribuido (${assigned}) tiene que coincidir con lo recibido (${quantity}).` },
      message: `Lo distribuido (${assigned}) tiene que coincidir con lo recibido (${quantity}).`,
    };
  }

  return { ok: true };
}

/** Divide la cantidad en partes iguales entre N variantes; el resto va a la última. */
export function splitEvenly(quantity: number, count: number): number[] {
  if (count <= 0) return [];
  if (!Number.isFinite(quantity) || quantity <= 0) return new Array(count).fill(0);
  const base = Math.floor((quantity / count) * 1_000_000) / 1_000_000;
  const parts = new Array(count).fill(base);
  const assigned = base * count;
  parts[count - 1] = Math.round((parts[count - 1] + (quantity - assigned)) * 1_000_000) / 1_000_000;
  return parts;
}
